import { Indicator, Item } from "@radix-ui/react-radio-group"
import clsx from "clsx"
import {
    MutableRefObject,
    KeyboardEvent as ReactKeyboardEvent,
    Ref,
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useRef,
} from "react"

import { ifSpaceOrEnter } from "@utils/keyboard"

import { Skeleton } from "@particles/index"

import { Label } from ".."
import styles from "./RadioItem.module.scss"
import { RadioItemProps } from "./RadioItem.types"

function RadioItem(props: RadioItemProps, ref: Ref<HTMLButtonElement>) {
    const {
        id,

        value,

        variant = "default",

        label = "",
        showLabel = false,
        supportText = "",
        showSupportText = false,

        alignment = "left",

        isDisabled = false,
        isLoading = false,
        isError = false,
        isSelected = undefined,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isTabbable = true,

        ...rest
    } = props
    const defaultId = useId()

    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const setRefs = useCallback(
        (node: HTMLButtonElement | null) => {
            buttonRef.current = node
            if (typeof ref === "function") {
                ref(node)
            } else if (ref) {
                const mutableRef = ref as MutableRefObject<HTMLButtonElement | null>
                mutableRef.current = node
            }
        },
        [ref],
    )

    // Radix's RadioGroup intentionally suppresses Enter (per WAI-ARIA) and v1.1.3
    // also drops any onKeyDown passed via props, so we attach a native listener to
    // ensure both Enter and Space select the focused radio item.
    useEffect(() => {
        const button = buttonRef.current
        if (!button) {
            return
        }
        function handleKeyDown(event: KeyboardEvent) {
            ifSpaceOrEnter(event as unknown as ReactKeyboardEvent<HTMLButtonElement>, () => button?.click())
        }
        button.addEventListener("keydown", handleKeyDown)
        return () => button.removeEventListener("keydown", handleKeyDown)
    }, [])

    const renderContent = () => {
        return (
            <div className={styles.contentWrapper}>
                <div className={styles.labelWrapper}>
                    {isLoading ? (
                        <Skeleton
                            width={128}
                            height={10}
                            borderRadius={0}
                            containerClassName={styles[`${alignment}Align`]}
                        />
                    ) : (
                        <Label label={label || value} htmlFor={id || defaultId} isDisabled={isDisabled} />
                    )}
                </div>
                {showSupportText && (
                    <div className={styles.supportTextWrapper}>
                        {isLoading ? (
                            <Skeleton width={272} height={10} borderRadius={0} />
                        ) : (
                            <span className={styles.supportText}>{supportText}</span>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <Item
            className={clsx(styles.base, styles[`${alignment}Align`], styles[variant], {
                [styles.checked]: isSelected,
                [styles.loading]: isLoading,
                [styles.error]: isError,
                [styles.disabled]: isDisabled,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
            })}
            value={value}
            {...(isSelected === undefined ? {} : { checked: isSelected })}
            disabled={isDisabled}
            id={id || defaultId}
            tabIndex={isTabbable && !isDisabled && !isLoading ? 0 : -1}
            {...rest}
            ref={setRefs}
        >
            {alignment === "right" && showLabel && renderContent()}
            <div className={styles.radioWrapper}>
                {isLoading ? (
                    <Skeleton circle={true} width={16} height={16} />
                ) : (
                    <div className={styles.radio}>
                        <Indicator className={styles.indicator} />
                    </div>
                )}
            </div>
            {alignment === "left" && showLabel && renderContent()}
        </Item>
    )
}

export default forwardRef(RadioItem)
